import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import classNames from 'classnames';
import type { IconName } from '@squareup/shared-ui-react';
import { ErrorBoundary } from '@squareup/shared-ui-error-boundary';
import { withI18n } from '@squareup/service-i18n/components';
import { MarketTheme } from '@squareup/market-react';
import { useResponsiveBreakpoint } from '@squareup/ui-responsive-window';
import errorTrackingClient from '../../../utils/error-tracking-client';
import type { MoneyUnion } from '../../../utils/format-currency';
import styles from './ProductPageHeader.module.css';
import { FolderProgressBar } from './folder-progress-bar/FolderProgressBar';
import {
  Balance,
  Banners,
  ButtonDropdown,
  Buttons,
  Error,
  GoBack,
  Loading,
  ProductPageLocationSelector,
  Settings,
  AccountButton,
} from './ProductPageHeaderComponents';

export interface ProductPageHeaderProps {
  readonly className?: string;
  readonly secondaryClassName?: string;
  readonly title: string;
  readonly shouldCenterTitle?: boolean;
  readonly locationSelector?: JSX.Element;
  readonly currentUnitToken?: string;
  readonly units?: Subunit[];
  readonly handleSelectLocation?: (subunit: Subunit) => void;
  readonly goBack?: () => void;
  readonly subtitle?: string;
  readonly goToSettings?: () => void;
  readonly settingsMenuRows?: SettingsMenuRow[];
  readonly settingsIconType?: 'settings' | 'help';
  readonly headline?: string;
  readonly balance?: MoneyUnion;
  readonly balanceSuperText?: string;
  readonly balanceSubText?: string | ReactNode;
  readonly balanceSubTextTooltip?: BalanceSubTextTooltip;
  readonly progressData?: ProgressData;
  readonly buttons?: HeaderButton[];
  readonly buttonDropdown?: HeaderDropdown;
  readonly account?: AccountProps;
  readonly banners?: BannerProps[];
  readonly isLoading?: boolean;
  readonly hasError?: boolean;
  readonly onTryAgain?: () => void;
}

export interface SettingsMenuRow {
  onClick: () => void;
  title: string;
  icon?: IconName;
  className?: string;
}

export interface Subunit {
  readonly nickname: string;
  readonly token: string;
}

interface ProgressData {
  target: MoneyUnion;
  availableBalance: MoneyUnion;
}

export interface BalanceSubTextTooltip {
  availableAmount?: MoneyUnion;
  pendingAmount?: MoneyUnion;
}

export interface HeaderButton {
  readonly copy: string;
  readonly onClick: () => void;
  readonly icon?: IconName;
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
}

export interface HeaderDropdown {
  readonly copy: string;
  readonly contentList: ListProps | JSX.Element;
  readonly isDisabled?: boolean;
  readonly onClick?: () => void;
}

export interface ListProps {
  readonly className?: string;
  readonly slot?: string;
  readonly options: ListOption[];
}

interface ListOption {
  readonly key?: string;
  readonly icon?: IconName;
  readonly label: string;
  readonly onClick?: () => void;
}

interface BannerAction {
  readonly elementType: 'button' | 'link';
  readonly href?: string;
  readonly onClick?: () => void;
  readonly text: string;
}

export interface BannerProps {
  readonly key: string;
  readonly variant: 'info' | 'success' | 'warning' | 'critical';
  readonly className?: string;
  readonly title?: string;
  readonly description?: string;
  readonly descriptionHtml?: string;
  readonly actions?: BannerAction[];
}

export interface AccountProps {
  readonly accountSuffix?: string;
  readonly accountNumber?: string;
  readonly routingNumber?: string;
}

interface MainContentProps extends ProductPageHeaderProps {
  readonly isSecondaryCentered?: boolean;
}

const MainContent = (props: MainContentProps) => {
  const {
    title,
    shouldCenterTitle,
    currentUnitToken,
    units,
    handleSelectLocation,
    goBack,
    locationSelector,
    subtitle,
    account,
    goToSettings,
    settingsMenuRows,
    settingsIconType,
    headline,
    balance,
    balanceSuperText,
    balanceSubText,
    balanceSubTextTooltip,
    progressData,
    buttons,
    buttonDropdown,
    banners,
    isLoading,
    hasError,
    onTryAgain,
    isSecondaryCentered,
  } = props;
  const { isMobile, isDesktop } = useResponsiveBreakpoint();
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.titleCell}>
        <div className={shouldCenterTitle ? styles.titleCentered : ''}>
          <div className={styles.titleAndLocation}>
            <span className={styles.title}>{title}</span>
            <ProductPageLocationSelector
              currentUnitToken={currentUnitToken}
              units={units}
              handleSelectLocation={handleSelectLocation}
              locationSelector={locationSelector}
            />
          </div>
          {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
        </div>
      </div>

      {hasError ? <Error t={t} onTryAgain={onTryAgain} /> : isLoading ? <Loading /> : null}

      {!isLoading && !hasError && (
        <>
          {account && <AccountButton t={t} account={account} />}
          {!isSecondaryCentered && <Settings
            t={t}
            goToSettings={goToSettings}
            settingsMenuRows={settingsMenuRows}
            iconType={settingsIconType}
          />}
          {headline && (
            <>
              <div aria-hidden className={styles.spacerTop} />
              <div className={`display-10 ${styles.headline}`}>{headline}</div>
            </>
          )}
          {balance && (
            <Balance
              t={t}
              balance={balance}
              balanceSuperText={balanceSuperText}
              balanceSubText={balanceSubText}
              balanceSubTextTooltip={balanceSubTextTooltip}
              shouldAddBottomSpacer={(!isDesktop && !!buttons)}
            />
          )}
          {progressData && (
            <div className={styles.progressBar}>
              <FolderProgressBar target={progressData.target} balance={progressData.availableBalance} />
            </div>
          )}
          {buttons && <Buttons buttons={buttons} isMobile={isMobile} shouldWrap={!!goBack || buttons.length > 2} />}
          {buttonDropdown && (<ButtonDropdown buttonDropdown={buttonDropdown} />)}
          {banners && banners.length > 0 && <Banners banners={banners} />}
        </>
      )}
    </>
  );
};

export const ProductPageHeader = (props: ProductPageHeaderProps) => {
  const {
    className = '',
    secondaryClassName = '',
    onTryAgain,
    goBack,
    goToSettings,
    settingsMenuRows,
    settingsIconType,
  } = props;
  const { isMobile } = useResponsiveBreakpoint();
  const { t } = useTranslation();

  /* Styling differs between mobile and desktop:
   * Larger screens: variant styles (headerPrimary/secondaryClassName) applied to inner grid wrapper
   * Mobile: variant styles applied directly to header (no wrapper)
   */
  const variantClass = secondaryClassName || styles.headerPrimary;
  const isSecondarySpread = secondaryClassName === styles.headerSecondarySpread;
  const isSecondaryCentered = secondaryClassName === styles.headerSecondaryCentered;
  const headerClass = classNames(styles.header, className, isMobile && variantClass);
  const mainContentClass = classNames(styles.mainContent, isSecondarySpread && styles.headerSecondarySpreadMainContent);

  return (
    <MarketTheme>
      <ErrorBoundary
        errorTrackingClient={errorTrackingClient}
        fallback={<Error t={t} onTryAgain={onTryAgain} />}
      >
        <header className={headerClass}>
          {goBack && <GoBack t={t} goBack={goBack} />}
          {!isMobile
            ? <div className={mainContentClass}>
              <div className={variantClass}>
                <MainContent isSecondaryCentered={isSecondaryCentered} {...props} />
              </div>
            </div>
            : <MainContent isSecondaryCentered={isSecondaryCentered} {...props} />}
          {isSecondaryCentered &&
            <Settings
              t={t}
              goToSettings={goToSettings}
              settingsMenuRows={settingsMenuRows}
              iconType={settingsIconType}
            />
          }
        </header>
      </ErrorBoundary>
    </MarketTheme>
  );
};

export const ProductPageHeaderI18n = withI18n(ProductPageHeader);
